// =============================================================================
// FILE:
// frontend/src/pms/task_execution/drawers/RequestMaterialDrawer.js
// =============================================================================

import React, {
  useState
} from "react";

import {
  X
} from "lucide-react";

import {
  createMaterialRequest
} from "../services/taskMaterialRequestService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function RequestMaterialDrawer({

  open,

  task,

  material,

  onClose,

  onSuccess

}) {


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [requestQty, setRequestQty] =
    useState("");


  const [remarks, setRemarks] =
    useState("");


  const [saving, setSaving] =
    useState(false);


  const [error, setError] =
    useState("");


  // ===========================================================================
  // CLOSE
  // ===========================================================================
  if (!open || !material) {
    return null;
  }


  // ===========================================================================
  // VALIDATE
  // ===========================================================================
  const validate =
    () => {

      const qty =
        parseFloat(requestQty || 0);

      if (qty <= 0) {

        return "Request qty must be greater than zero";
      }

    }
      

  // ===========================================================================
  // SAVE
  // ===========================================================================
  const handleSave =
    async () => {

      try {

        setError("");

        const validationError =
          validate();

        if (validationError) {

          setError(validationError);

          return;
        }

        setSaving(true);


        // ---------------------------------------------------------------
        // PAYLOAD
        // ---------------------------------------------------------------
        const payload = {

  project_id:
    material.project_id,

  phase_id:
    material.phase_id,

  task_id:
    material.task_id,

  priority:
    "MEDIUM",

  remarks,

  materials: [

    {
      task_material_plan_id:
        material.id,

      reservation_id:
        material.reservation_id,

      requested_qty:
        parseFloat(requestQty),

      remarks
    }
  ]
};


        // ---------------------------------------------------------------
        // API
        // ---------------------------------------------------------------
        const response =
          await createMaterialRequest(
            payload
          );


        if (response?.success) {

          if (onSuccess) {

            onSuccess(response);
          }

          onClose();

        } else {

          setError(
            response?.message ||

            "Failed to create request"
          );
        }

      } catch (err) {

        console.error(err);

        setError(
          "Failed to create request"
        );

      } finally {

        setSaving(false);
      }
    };


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.overlay}>


      <div style={styles.drawer}>


        {/* ============================================================ */}
        {/* HEADER */}
        {/* ============================================================ */}
        <div style={styles.header}>


          <div>

            <div style={styles.title}>

              Request Material

            </div>


            <div style={styles.subtitle}>


              Create material request
              for task execution

            </div>

          </div>


          <button
            style={styles.closeBtn}
            onClick={onClose}
          >

            <X size={18} />

          </button>

        </div>


        {/* ============================================================ */}
        {/* BODY */}
        {/* ============================================================ */}
        <div style={styles.body}>


          {/* -------------------------------------------------------- */}
          {/* MATERIAL INFO */}
          {/* -------------------------------------------------------- */}
          <div style={styles.card}>


            <div style={styles.sectionTitle}>

              Material Information

            </div>


            <InfoRow
              label="Material"
              value={
                material.material_description
              }
            />


            <InfoRow
              label="Material Code"
              value={
                material.material_code
              }
            />


            <InfoRow
              label="Planned Qty"
              value={
                material.planned_qty
              }
            />


            <InfoRow
              label="Reconciled Qty"
              value={
                material.reconciled_qty
              }
            />


            <InfoRow
              label="Issued Qty"
              value={
                material.issued_qty
              }
            />


            <InfoRow
              label="Requestable Qty"
              value={
                material.requestable_qty
              }
            />

          </div>


          {/* -------------------------------------------------------- */}
          {/* REQUEST FORM */}
          {/* -------------------------------------------------------- */}
          <div style={styles.card}>


            <div style={styles.sectionTitle}>

              Request Details

            </div>


            {/* ---------------------------------------------------- */}
            {/* REQUEST QTY */}
            {/* ---------------------------------------------------- */}
            <div style={styles.formGroup}>


              <label style={styles.label}>

                Request Qty

              </label>


              <input
                type="number"
                value={requestQty}
                onChange={(e) =>
                  setRequestQty(
                    e.target.value
                  )
                }
                style={styles.input}
              />

            </div>


            {/* ---------------------------------------------------- */}
            {/* REMARKS */}
            {/* ---------------------------------------------------- */}
            <div style={styles.formGroup}>


              <label style={styles.label}>

                Remarks

              </label>


              <textarea
                value={remarks}
                onChange={(e) =>
                  setRemarks(
                    e.target.value
                  )
                }
                style={styles.textarea}
              />

            </div>


            {/* ---------------------------------------------------- */}
            {/* ERROR */}
            {/* ---------------------------------------------------- */}
            {

              error && (

                <div style={styles.errorBox}>


                  {error}

                </div>
              )
            }

          </div>

        </div>


        {/* ============================================================ */}
        {/* FOOTER */}
        {/* ============================================================ */}
        <div style={styles.footer}>


          <button
            style={styles.cancelBtn}
            onClick={onClose}
          >

            Cancel

          </button>


          <button
            style={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >

            {

              saving

                ?

                "Saving..."

                :

                "Create Request"
            }

          </button>

        </div>

      </div>

    </div>
  );
}


// =============================================================================
// INFO ROW
// =============================================================================

function InfoRow({

  label,

  value

}) {

  return (

    <div style={styles.infoRow}>


      <div style={styles.infoLabel}>

        {label}

      </div>


      <div style={styles.infoValue}>

        {value || "-"}

      </div>

    </div>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  overlay: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(0,0,0,0.35)",

    display: "flex",

    justifyContent: "flex-end",

    zIndex: 99999
  },

  drawer: {

    width: 520,

    height: "100vh",

    background: "#f5f7fb",

    display: "flex",

    flexDirection: "column"
  },

  header: {

    background: "#ffffff",

    padding: 20,

    borderBottom:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  title: {

    fontSize: 20,

    fontWeight: 700,

    color: "#111827"
  },

  subtitle: {

    marginTop: 6,

    fontSize: 13,

    color: "#6b7280"
  },

  closeBtn: {

    width: 36,

    height: 36,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    background: "#fff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    cursor: "pointer"
  },

  body: {

    flex: 1,

    overflow: "auto",

    padding: 20,

    display: "flex",

    flexDirection: "column",

    gap: 20
  },

  card: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  sectionTitle: {

    fontSize: 16,

    fontWeight: 700,

    marginBottom: 18,

    color: "#111827"
  },

  infoRow: {

    display: "flex",

    justifyContent: "space-between",

    padding: "10px 0",

    borderBottom:
      "1px solid #f3f4f6"
  },

  infoLabel: {

    fontSize: 13,

    color: "#6b7280"
  },

  infoValue: {

    fontSize: 14,

    fontWeight: 600,

    color: "#111827"
  },

  formGroup: {

    display: "flex",

    flexDirection: "column",

    marginBottom: 18
  },

  label: {

    fontSize: 13,

    fontWeight: 600,

    color: "#374151",

    marginBottom: 8
  },

  input: {

    height: 42,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    padding: "0 12px",

    outline: "none"
  },

  textarea: {

    minHeight: 120,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    padding: 12,

    resize: "vertical",

    outline: "none"
  },

  errorBox: {

    marginTop: 10,

    padding: 12,

    borderRadius: 8,

    background: "#fee2e2",

    color: "#991b1b",

    fontSize: 13,

    fontWeight: 600
  },

  footer: {

    background: "#ffffff",

    borderTop:
      "1px solid #e5e7eb",

    padding: 16,

    display: "flex",

    justifyContent: "flex-end",

    gap: 12
  },

  cancelBtn: {

    height: 40,

    border: "1px solid #d1d5db",

    background: "#ffffff",

    borderRadius: 8,

    padding: "0 16px",

    cursor: "pointer"
  },

  saveBtn: {

    height: 40,

    border: "none",

    background: "#2563eb",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 18px",

    cursor: "pointer",

    fontWeight: 700
  }
};